import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RapidSOSMark } from '@/components/RapidSOSMark/RapidSOSMark';
import { RapidSOSWordmark } from '@/components/RapidSOSMark/RapidSOSWordmark';
import { DEFAULT_MARK_ANIMATION } from '@/components/RapidSOSMark/animations';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';

import { AppOpeningScreen } from './AppOpeningScreen';
import { afterSwitch, isAppSignOut, readAppSignInRequest, requestAppToken, type AppSignInOutcome } from './appSignIn';

type Shown = 'working' | Exclude<AppSignInOutcome['kind'], 'go' | 'switch'> | 'invalid';

/** Signs a visitor in to a published app or the apps portal (see `appSignIn.ts`). */
export function AppSignInPage() {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, loginWithRedirect, logout } = useAuth();
  const signOut = useMemo(() => isAppSignOut(window.location.search), []);
  const request = useMemo(() => readAppSignInRequest(window.location.search), []);
  const [shown, setShown] = useState<Shown>(request || signOut ? 'working' : 'invalid');
  const [attempt, setAttempt] = useState(0);
  const startedRef = useRef(-1);

  useEffect(() => {
    if (!signOut || isLoading) return;
    void logout({ logoutParams: { returnTo: window.location.origin } });
  }, [signOut, isLoading, logout]);

  useEffect(() => {
    if (signOut) return;
    if (!request || isLoading || startedRef.current === attempt) return;
    startedRef.current = attempt;
    const here = `${window.location.pathname}${window.location.search}`;
    if (!isAuthenticated) {
      void loginWithRedirect({ appState: { returnTo: here } }).catch(() => setShown('failed'));
      return;
    }
    setShown('working');
    void requestAppToken(request).then((outcome) => {
      if (outcome.kind === 'go') {
        window.location.replace(outcome.url);
        return;
      }
      if (outcome.kind === 'switch') {
        void loginWithRedirect({
          appState: { returnTo: afterSwitch(window.location.pathname, window.location.search) },
          authorizationParams: { organization: outcome.organizationId },
        }).catch(() => setShown('failed'));
        return;
      }
      setShown(outcome.kind);
    });
  }, [attempt, isAuthenticated, isLoading, loginWithRedirect, request, signOut]);

  if (shown === 'working') return signOut ? null : <AppOpeningScreen portal={request?.kind === 'portal'} />;
  const message = t(`auth.app-sign-in.${shown}`);
  return (
    <main className="flex min-h-full w-full items-center justify-center bg-background px-6 py-16">
      <div className="w-full max-w-sm text-center">
        <div className="flex items-center justify-center gap-2 text-foreground">
          <RapidSOSMark className="h-6 w-6" animation={DEFAULT_MARK_ANIMATION} />
          <RapidSOSWordmark className="h-6" />
        </div>
        <h1 className="mt-8 text-2xl font-semibold tracking-tight text-foreground">{t('auth.app-sign-in.title')}</h1>
        <p role="alert" className="mt-3 text-sm text-muted-foreground">{message}</p>
        <div className="mt-6 flex items-center justify-center gap-3">
          {shown === 'failed' ? (
            <Button onClick={() => setAttempt((value) => value + 1)}>{t('auth.app-sign-in.retry')}</Button>
          ) : null}
          <Button variant="outline" onClick={() => window.location.assign('/')}>{t('auth.app-sign-in.backToStudio')}</Button>
        </div>
      </div>
    </main>
  );
}
